// What a cloud run reads: sources with shares, an order, a size. The catalogue is a few
// openly licensed bodies of text; links and uploads join it as the user's own sources.

const SIZES = [1, 3, 6, 12, 24, 48];

function pct(share, total) {
  return total > 0 ? Math.round((share / total) * 100) : 0;
}

export function estimate({ gigabytes, offer, width, speeds }) {
  // hours from the closest-width speed test on the same gpu; none measured, no guess
  if (!offer || !speeds?.length) return null;
  const same = speeds.filter(s => s.gpu_name === offer.gpu_name);
  if (!same.length) return null;
  const nearest = same.reduce((a, b) => (Math.abs(b.width - width) < Math.abs(a.width - width) ? b : a));
  const hours = (gigabytes * 1e9) / nearest.bytes_per_second / 3600;
  const price = offer.price_per_hour ?? offer.dph_total;
  return { hours, dollars: hours * price, width: nearest.width };
}

function bytesLabel(n) {
  if (!n) return 'size unknown';
  return n >= 1e9 ? `${(n / 1e9).toFixed(1)} gb` : `${Math.max(1, Math.round(n / 1e6))} mb`;
}

export default function DataMix({ catalogue, orders, value, onChange, mine = [] }) {
  const chosen = new Map(value.sources.map(s => [s.id, s.share]));
  const total = value.sources.reduce((a, s) => a + Number(s.share || 0), 0);
  const toggle = (id) => {
    const sources = chosen.has(id) ? value.sources.filter(s => s.id !== id) : [...value.sources, { id, share: 1 }];
    onChange({ ...value, sources });
  };
  const setShare = (id, share) => onChange({
    ...value, sources: value.sources.map(s => (s.id === id ? { ...s, share: Math.max(0, Number(share) || 0) } : s)),
  });
  const everything = [
    ...catalogue.map(c => ({ ...c, size: `${c.gigabytes} gb` })),
    ...mine.map(m => ({ id: m.id, label: m.label, licence: m.licence || 'licence not given', mine: true,
      about: m.streamed ? 'yours · streamed from hugging face' : 'yours', size: bytesLabel(m.bytes) })),
  ];
  const licences = [...new Set(everything.filter(c => chosen.has(c.id)).map(c => c.licence))];
  const unpublishable = everything.some(c => chosen.has(c.id) && c.mine);

  return (
    <div className="space-y-4" data-testid="data-mix">
      <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]">
        {everything.map(c => {
          const on = chosen.has(c.id);
          return (
            <div key={c.id} className="py-2.5 flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => toggle(c.id)} aria-pressed={on} aria-label={`use ${c.label}`}
                className="w-4 h-4 rounded-sm shrink-0"
                style={{ border: `1px solid ${on ? '#ffffff' : '#5a615a'}`, background: on ? '#ffffff' : 'transparent' }}
                data-testid={`source-${c.id}`} />
              <button type="button" onClick={() => toggle(c.id)} className="text-left min-w-0 flex-1">
                <span className={`font-mono text-sm lowercase ${on ? 'text-white' : 'text-[#aaaaaa]'}`}>{c.label}</span>
                <span className="font-mono text-[11px] text-[#555555] lowercase ml-2">{c.size} · {c.licence}</span>
                <span className="block font-mono text-[11px] text-[#666666] lowercase mt-0.5">{c.about}</span>
              </button>
              {on && value.sources.length > 1 && (
                <label className="flex items-center gap-1.5 font-mono text-xs text-[#888888] shrink-0">
                  share
                  <input type="number" min="0" step="1" value={chosen.get(c.id)} onChange={e => setShare(c.id, e.target.value)}
                    className="w-14 bg-transparent border border-[#383f38] rounded px-1.5 py-0.5 text-white text-right outline-none focus:border-white" />
                  <span className="w-9 text-right text-white">{pct(chosen.get(c.id), total)}%</span>
                </label>
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {Object.keys(orders).map(o => (
            <button key={o} type="button" onClick={() => onChange({ ...value, order: o })}
              className={`border px-3 py-1.5 rounded-full font-mono text-xs lowercase transition-all ${
                value.order === o ? 'border-white text-white bg-white/10' : 'border-[#383f38] text-[#888888] hover:text-white hover:border-[#888888]'}`}>
              {{ graded: 'easy to hard', mixed: 'mixed', sequential: 'in turn' }[o] || o}
            </button>
          ))}
        </div>
        <p className="font-mono text-[11px] text-[#555555] lowercase">{orders[value.order]}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {SIZES.map(g => (
          <button key={g} type="button" onClick={() => onChange({ ...value, gigabytes: g })}
            className={`border px-3 py-1.5 rounded-full font-mono text-xs lowercase transition-all ${
              value.gigabytes === g ? 'border-white text-white bg-white/10' : 'border-[#383f38] text-[#888888] hover:text-white hover:border-[#888888]'}`}>
            {g} gb
          </button>
        ))}
      </div>
      {licences.length > 0 && (
        <p className="font-mono text-[11px] text-[#555555] lowercase">
          licences: {licences.join(', ')}.{' '}
          {unpublishable ? 'it reads your own sources: check their licences before publishing what it learns.' : 'a model trained on these can be published.'}
        </p>
      )}
    </div>
  );
}
